/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { ReactElement } from 'react';

import { Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import type { SectionName } from '@coursera/event-pulse-types';

import Markdown from 'bundles/cms/components/Markdown';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';

type CareerInsightCardProps = {
  title?: string | null;
  description?: string | null;
  trackingSection?: SectionName;
};

type ListItemProps = {
  children?: Array<string | ReactElement>;
};

type ListProps = {
  children?: React.ReactElement<ListItemProps>[];
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    height: 100%;
    padding: 20px var(--cds-spacing-400);
    background-color: var(--cds-color-blue-50);
    border-radius: 8px;

    .rc-Markdown.styled {
      h1,
      h2,
      h3,
      h4,
      h5,
      h6,
      p {
        font-family: var(--cds-font-family-source-sans-pro);
      }

      p {
        ${typography2.bodySecondary}
      }

      a {
        &:hover,
        &:focus {
          text-decoration: none;
        }

        &:visited {
          color: var(--cds-color-purple-950);
        }
      }

      ul {
        list-style: none;
        margin: unset;
        padding: unset;
      }
    }

    ${breakpoints.down('sm')} {
      background-color: var(--cds-color-neutral-background-primary);
    }
  `,
  title: css`
    width: 100%;
    padding-bottom: 12px;
    margin-bottom: 24px;
    border-bottom: 2px solid var(--cds-color-grey-50);
  `,
  listItem: css`
    border-bottom: 1px solid var(--cds-color-grey-50);
    margin-bottom: 10px;
    padding-bottom: 10px;
  `,
  listItemHeading: css`
    margin: 0 !important;
  `,
  markdownContainer: css`
    width: 100%;
  `,
};

const listRenderer = {
  list: (props: ListProps) => {
    return (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles
      <ul role="list">
        {props?.children?.map((child) => (
          // eslint-disable-next-line jsx-a11y/no-redundant-roles
          <li role="listitem" css={styles.listItem} key={child.key}>
            <Typography2 component="p" variant="bodySecondary" css={styles.listItemHeading}>
              {child?.props?.children?.[0]}
            </Typography2>
          </li>
        ))}
      </ul>
    );
  },
};

const CareerRolesCard = ({ title, description, trackingSection }: CareerInsightCardProps) => {
  const theme = useDegreeDescriptionPageTheme();
  const { id } = useDegreeDescriptionPageData();
  const degreeId = id.replace(/^base~/, '');

  return (
    <div css={styles.container}>
      {title && (
        <Typography variant="h2semibold" css={[styles.title, theme.mobileFontSizeStyle?.subHeader]} component="h3">
          {title}
        </Typography>
      )}
      {description && (
        <Markdown
          source={description}
          renderers={listRenderer}
          trackingName="career_section_link"
          buttonTrackingNameV3="degree_info_link"
          css={styles.markdownContainer}
          trackingSection={trackingSection}
          trackingProductId={degreeId}
        />
      )}
    </div>
  );
};

export default React.memo(CareerRolesCard);
